// src/pages/admin/Page_Dashboard/Page_DashboardTeachers.js
//
// M06 Fase 6 - Pagina de detalle de profesores. Antes era un stub.
// Lista paginada con filtros locales por nombre/email/institucion.

import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Table, Input, Tag, message, Avatar, Empty } from "antd";
import {
  ReadOutlined,
  CheckCircleOutlined,
  AreaChartOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useAuth } from "../../../hooks/useAuth";
import { dashboardApi } from "../../../api/dashboardAPI";
import { PageHeader, EducaThemeProvider } from "../../../ui";
import { KpiCard } from "../../../components/Admin/Dashboard/_shared";
import "./Page_Dashboard.scss";

export function Page_DashboardTeachers() {
  const { accessToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const data = await dashboardApi.getTeachersList(accessToken);
        if (alive) setPayload(data);
      } catch (e) {
        const msg =
          e?.body?.message || e?.message || "No se pudo cargar el listado";
        if (alive) {
          setError(msg);
          message.error(msg);
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [accessToken]);

  const items = useMemo(() => {
    const list = payload?.items || [];
    const s = search.trim().toLowerCase();
    if (!s) return list;
    return list.filter((t) => {
      const fields = [t.nombre, t.email, t.institution?.nombre]
        .filter(Boolean)
        .map((v) => String(v).toLowerCase());
      return fields.some((f) => f.includes(s));
    });
  }, [payload, search]);

  const totals = payload?.totals || {};

  const kpis = [
    {
      key: "total",
      title: "Profesores totales",
      value: totals.total ?? 0,
      icon: <ReadOutlined />,
    },
    {
      key: "withRoutes",
      title: "Con rutas creadas",
      subtitle: "Profesores activos en contenido",
      value: totals.withRoutes ?? 0,
      icon: <CheckCircleOutlined />,
    },
    {
      key: "avgRoutes",
      title: "Promedio rutas / profesor",
      value: totals.avgRoutesPerTeacher ?? 0,
      icon: <AreaChartOutlined />,
    },
  ];

  const columns = [
    {
      title: "Profesor",
      dataIndex: "nombre",
      sorter: (a, b) => (a?.nombre || "").localeCompare(b?.nombre || ""),
      render: (v, row) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Avatar
            size={32}
            src={row.avatar || undefined}
            icon={!row.avatar ? <ReadOutlined /> : undefined}
          />
          <div>
            <div style={{ fontWeight: 600 }}>{v || "(sin nombre)"}</div>
            <div style={{ fontSize: 12, color: "#6b7280" }}>
              {row.email || "—"}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Institucion",
      dataIndex: ["institution", "nombre"],
      sorter: (a, b) =>
        (a?.institution?.nombre || "").localeCompare(
          b?.institution?.nombre || "",
        ),
      render: (v) => v || <Tag color="default">Sin institucion</Tag>,
    },
    {
      title: "Clases",
      dataIndex: "classnumbers",
      render: (v) => v || "-",
      width: 120,
    },
    {
      title: "Rutas didacticas",
      dataIndex: "rutasDidacticas",
      align: "right",
      sorter: (a, b) => (a?.rutasDidacticas || 0) - (b?.rutasDidacticas || 0),
      width: 140,
    },
    {
      title: "Alumnos asignados",
      dataIndex: "studentsAssigned",
      align: "right",
      sorter: (a, b) => (a?.studentsAssigned || 0) - (b?.studentsAssigned || 0),
      width: 160,
    },
    {
      title: "Fecha alta",
      dataIndex: "created_at",
      sorter: (a, b) =>
        new Date(a?.created_at || 0) - new Date(b?.created_at || 0),
      render: (v) => (v ? dayjs(v).format("DD/MM/YYYY") : "—"),
      width: 140,
    },
  ];

  return (
    <EducaThemeProvider>
      <div className="page-dashboard">
        <PageHeader
          title="Profesores"
          subtitle="Listado completo de profesores con sus instituciones y carga"
        />

        {error ? (
          <div
            style={{
              padding: 12,
              background: "rgba(198,63,62,0.08)",
              color: "#c63f3e",
              borderRadius: 12,
              marginBottom: 16,
            }}
          >
            {error}
          </div>
        ) : null}

        <Row gutter={[16, 16]}>
          {kpis.map((k) => (
            <Col key={k.key} xs={24} sm={12} md={8}>
              <KpiCard
                title={k.title}
                value={k.value}
                subtitle={k.subtitle}
                icon={k.icon}
                loading={loading}
              />
            </Col>
          ))}
        </Row>

        <div style={{ marginTop: 16, marginBottom: 12 }}>
          <Input.Search
            placeholder="Buscar por nombre, email o institucion"
            allowClear
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: 420 }}
          />
        </div>

        <Table
          rowKey={(r) => r._id}
          loading={loading}
          dataSource={items}
          columns={columns}
          size="middle"
          pagination={{ pageSize: 25, showSizeChanger: true }}
          locale={{
            emptyText: <Empty description="No se encontraron profesores" />,
          }}
          scroll={{ x: 1100 }}
        />
      </div>
    </EducaThemeProvider>
  );
}
